import React, {useMemo} from 'react';
import {Box, styled} from '@mui/material';
import {SocialPlatform} from '@/types/platforms';
import SocialNetworkButton from './SocialNetworkButton';
import ConnectedSocialAccount, {
  ConnectedAccountData,
} from './ConnectedSocialAccount';
import {platformToProviderMap, providerToPlatformMap} from '@/config/constants';
import {User} from '@/types/user';
import ErrorBanner from './ErrorBanner';

export interface PlatformConnectionData {
  platform: SocialPlatform;
  isConnected: boolean;
  isLoading?: boolean;
  accountData?: ConnectedAccountData;
  hasError?: boolean;
}

export interface SocialNetworksListProps {
  platforms: SocialPlatform[];
  connectedPlatforms: SocialPlatform[];
  user?: User;
  onConnect: (platform: SocialPlatform) => void;
  onUnlink: (provider: string) => void;
  onRelink?: (provider: string) => void;
  isLoading?: boolean;
  loadingPlatform?: SocialPlatform | null;
  errorProvider?: string;
  errorMessage?: string;
  onDismissError?: () => void;
}

const StyledContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

function SocialNetworksList({
  platforms,
  connectedPlatforms,
  user,
  onConnect,
  onUnlink,
  onRelink,
  isLoading = false,
  loadingPlatform,
  errorProvider,
  errorMessage,
  onDismissError,
}: SocialNetworksListProps) {
  // Create platform connection data with account details
  const platformConnectionData = useMemo((): PlatformConnectionData[] => {
    // Find the platform that matches the error provider
    const errorPlatform = errorProvider
      ? providerToPlatformMap[errorProvider]
      : undefined;

    return platforms.map((platform) => {
      const isConnected = connectedPlatforms.includes(platform);
      const platformProvider = platformToProviderMap[platform];

      // Find the corresponding identity for this platform
      const identity = user?.identities?.find(
        (userIdentity) => userIdentity.provider === platformProvider
      );

      // Create account data if connected
      const accountData =
        isConnected && identity
          ? {
              provider: platformProvider,
              cachedProfileImageUrl: identity.cachedProfileImageUrl,
              cachedHandle: identity.cachedHandle,
              needsRelink: identity.needsRelink || false,
            }
          : undefined;

      return {
        platform,
        isConnected,
        isLoading: loadingPlatform === platform,
        accountData,
        hasError: errorPlatform === platform,
      };
    });
  }, [
    platforms,
    connectedPlatforms,
    user?.identities,
    loadingPlatform,
    errorProvider,
  ]);

  const handleConnect = (platform: SocialPlatform) => {
    if (!isLoading) {
      onConnect(platform);
    }
  };

  const handleUnlink = (provider: string) => {
    if (!isLoading) {
      onUnlink(provider);
    }
  };

  const handleRelink = (provider: string) => {
    if (!isLoading && onRelink) {
      onRelink(provider);
    }
  };

  return (
    <StyledContainer>
      {platformConnectionData.map(
        ({
          platform,
          isConnected,
          isLoading: platformLoading,
          accountData,
          hasError,
        }) => {
          return (
            <React.Fragment key={platform}>
              {/* Show error banner above the specific problematic platform */}
              {hasError && !isConnected && errorMessage && onDismissError && (
                <ErrorBanner
                  message={errorMessage}
                  onDismiss={onDismissError}
                />
              )}

              {isConnected && accountData ? (
                // Render connected account
                <ConnectedSocialAccount
                  platform={platform}
                  accountData={accountData}
                  onUnlink={handleUnlink}
                  onRelink={handleRelink}
                  isLoading={platformLoading}
                />
              ) : (
                // Render connection button for unconnected platforms
                <SocialNetworkButton
                  platform={platform}
                  isLoading={platformLoading}
                  onClick={() => handleConnect(platform)}
                  disabled={isLoading}
                  hasError={hasError}
                />
              )}
            </React.Fragment>
          );
        }
      )}
    </StyledContainer>
  );
}

export default SocialNetworksList;
